import type { CSSProperties, FC } from 'react'
import type { BoardReading, Service } from '@/constants/services'

import { useState } from 'react'
import { ArrowUpRight, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { InView } from '@/components/kit/in-view'
import { PageLink } from '@/components/kit/page-link'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { services } from '@/constants/services'

/* ------------------------------- readings ------------------------------- */

const Pressure: FC = () => {
    return (
        <InView className='relative h-full w-full'>
            <svg
                viewBox='0 0 300 120'
                className='h-full w-full overflow-visible'
                aria-hidden='true'
            >
                {[0, 1, 2, 3].map(ring => (
                    <path
                        key={ring}
                        d={`M-10 ${100 - ring * 22} C 60 ${70 - ring * 20}, 140 ${120 - ring * 24}, 310 ${60 - ring * 18}`}
                        fill='none'
                        stroke='rgb(30 30 30 / 0.12)'
                        strokeWidth='1'
                        strokeDasharray='3 4'
                    />
                ))}
                <path
                    className='draw-path'
                    pathLength={1}
                    d='M0 104 L40 96 L80 98 L120 80 L160 72 L200 50 L240 38 L280 14'
                    fill='none'
                    stroke='#ff5c00'
                    strokeWidth='3'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ '--draw-delay': '200ms' } as CSSProperties}
                />
                <circle className='pop-in' cx='280' cy='14' r='6' fill='#ff5c00' style={{ '--delay': '1300ms' } as CSSProperties} />
                <text x='0' y='18' className='fill-ink font-mono' fontSize='11'>1031 hPa</text>
            </svg>
        </InView>
    )
}

const calendarCells = [0, 1, 0, 2, 1, 0, 1, 1, 0, 1, 2, 0, 1, 0, 1, 0, 1, 1, 2, 0, 1, 1, 0, 1, 0, 2, 1, 1]

const Calendar: FC = () => {
    return (
        <InView className='grid h-full w-full grid-cols-7 content-center gap-1.5'>
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                <span
                    key={`${day}-${index}`}
                    className='text-center font-mono text-[10px] text-stone'
                >
                    {day}
                </span>
            ))}
            {calendarCells.map((cell, index) => (
                <span
                    key={index}
                    className='relative aspect-square rounded-[5px] bg-ink/[0.06]'
                >
                    {cell > 0 && (
                        <span
                            className={cn('pop-in absolute inset-0 rounded-[5px]', cell === 2 ? 'bg-ink' : 'bg-ember')}
                            style={{ '--delay': `${150 + index * 45}ms` } as CSSProperties}
                        />
                    )}
                </span>
            ))}
        </InView>
    )
}

const barValues = [26, 34, 31, 48, 44, 63, 70, 88]

const Bars: FC = () => {
    return (
        <InView className='flex h-full w-full items-end gap-2'>
            {barValues.map((value, index) => (
                <span
                    key={index}
                    className={cn('bar-grow flex-1 rounded-t-[4px]', index === barValues.length - 1 ? 'bg-ember' : 'bg-ink/80')}
                    style={{ height: `${value}%`, '--delay': `${index * 90}ms` } as CSSProperties}
                />
            ))}
        </InView>
    )
}

const blips = [
    { x: '28%', y: '34%', delay: '0s' },
    { x: '64%', y: '26%', delay: '0.9s' },
    { x: '58%', y: '66%', delay: '1.7s' },
    { x: '22%', y: '70%', delay: '2.4s' }
]

const Radar: FC = () => {
    return (
        <div className='relative mx-auto aspect-square h-full'>
            <div className='absolute inset-0 rounded-full border border-ink/15' />
            <div className='absolute inset-[20%] rounded-full border border-ink/15' />
            <div className='absolute inset-[40%] rounded-full border border-ink/15' />
            <div className='absolute inset-x-0 top-1/2 h-px bg-ink/10' />
            <div className='absolute inset-y-0 left-1/2 w-px bg-ink/10' />
            <div className='absolute inset-0 animate-sweep rounded-full bg-[conic-gradient(from_0deg,rgb(255_92_0/0.35),rgb(255_92_0/0)_28%)]' />
            {blips.map(blip => (
                <span
                    key={blip.x}
                    className='absolute size-2.5 -translate-x-1/2 -translate-y-1/2'
                    style={{ left: blip.x, top: blip.y }}
                >
                    <span
                        className='ping absolute inset-0 rounded-full bg-ember'
                        style={{ animationDelay: blip.delay }}
                    />
                    <span className='relative block size-full rounded-full bg-ember' />
                </span>
            ))}
        </div>
    )
}

const Dial: FC = () => {
    const { ref, inView } = useInView<HTMLDivElement>()

    return (
        <div
            ref={ref}
            className='relative mx-auto h-full w-full max-w-[220px]'
        >
            <svg
                viewBox='0 0 200 110'
                className='h-full w-full overflow-visible'
                aria-hidden='true'
            >
                <path d='M14 100 A86 86 0 0 1 186 100' fill='none' stroke='rgb(30 30 30 / 0.12)' strokeWidth='14' strokeLinecap='round' />
                <path d='M14 100 A86 86 0 0 1 150 38' fill='none' stroke='#ff5c00' strokeWidth='14' strokeLinecap='round' />
                {['Cold', 'Mild', 'Warm', 'Hot'].map((label, index) => (
                    <text
                        key={label}
                        x={[12, 58, 128, 172][index]}
                        y={[118, 30, 30, 118][index]}
                        fontSize='10'
                        className='fill-stone font-mono'
                    >
                        {label}
                    </text>
                ))}
            </svg>
            <span
                className='absolute bottom-[9%] left-1/2 h-[62%] w-[3px] origin-bottom rounded-full bg-ink transition-transform duration-[1.6s] ease-expo'
                style={{ transform: `translateX(-50%) rotate(${inView ? 38 : -80}deg)` }}
            />
            <span className='absolute bottom-[5%] left-1/2 size-4 -translate-x-1/2 rounded-full bg-ink' />
        </div>
    )
}

const swatches = ['#ff5c00', '#1e1e1e', '#ffe27a', '#ebe6dd', '#f8f6f2']

const Swatches: FC = () => {
    const { ref, inView } = useInView<HTMLDivElement>()

    return (
        <div
            ref={ref}
            className='relative flex h-full w-full items-center justify-center'
        >
            {swatches.map((color, index) => (
                <span
                    key={color}
                    className='absolute h-[78%] w-[26%] rounded-xl border border-ink/10 shadow-[0_12px_24px_-14px_rgb(30_30_30/0.5)] transition-transform duration-[1.2s] ease-expo'
                    style={{
                        backgroundColor: color,
                        transitionDelay: `${index * 80}ms`,
                        transform: inView
                            ? `translateX(${(index - 2) * 52}%) rotate(${(index - 2) * 7}deg)`
                            : 'translateX(0) rotate(0deg)'
                    }}
                />
            ))}
        </div>
    )
}

const Reading: FC<{ board: BoardReading }> = ({ board }) => {
    if (board === 'pressure') return <Pressure />
    if (board === 'calendar') return <Calendar />
    if (board === 'bars') return <Bars />
    if (board === 'radar') return <Radar />
    if (board === 'dial') return <Dial />

    return <Swatches />
}

/* --------------------------------- tile --------------------------------- */

const Tile: FC<{ service: Service, delay: number, settle: number }> = ({ service, delay, settle }) => {
    const [open, setOpen] = useState(false)
    const listId = `${service.slug}-deliverables`

    return (
        <Reveal
            delay={delay}
            settle={settle}
            className='h-full'
        >
            <article
                data-open={open}
                className='group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-ink/10 bg-paper p-6 shadow-[0_24px_50px_-40px_rgb(30_30_30/0.5)] transition-[transform,box-shadow] duration-500 ease-expo hover:-translate-y-1 hover:shadow-[0_36px_60px_-36px_rgb(30_30_30/0.55)] md:p-7'
            >
                <svg
                    aria-hidden='true'
                    viewBox='0 0 400 400'
                    preserveAspectRatio='none'
                    className='pointer-events-none absolute inset-0 h-full w-full text-ink/[0.06]'
                >
                    {[0, 1, 2, 3, 4].map(ring => (
                        <ellipse
                            key={ring}
                            cx='330'
                            cy='60'
                            rx={70 + ring * 70}
                            ry={50 + ring * 55}
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='1.5'
                        />
                    ))}
                </svg>

                <div className='relative flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-stone'>
                    <span>{service.station}</span>
                    <span className='rounded-full border border-ink/12 px-2 py-0.5 text-ink'>{service.index}</span>
                </div>

                <div
                    aria-hidden='true'
                    className='relative mt-5 h-32 md:h-36'
                >
                    <Reading board={service.board} />
                </div>

                <p className='relative mt-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-ember'>
                    <span className='size-1.5 rounded-full bg-ember' />
                    {service.reading}
                </p>

                <h3 className='relative mt-3 text-[1.9rem] font-bold leading-[1] tracking-[-0.035em]'>
                    {service.title}
                </h3>
                <p className='relative mt-2 text-[15px] leading-relaxed text-stone'>
                    {service.short}
                </p>

                <div
                    className={cn(
                        'relative grid transition-[grid-template-rows] duration-500 ease-expo group-hover:grid-rows-[1fr] group-focus-within:grid-rows-[1fr]',
                        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    )}
                >
                    <div className='overflow-hidden'>
                        <ul
                            id={listId}
                            className='mt-5 flex flex-col border-t border-dashed border-ink/20 pt-4'
                        >
                            {service.deliverables.slice(0, 4).map(item => (
                                <li
                                    key={item}
                                    className='flex items-start gap-2.5 py-1 text-[14px] text-ink'
                                >
                                    <span className='mt-[7px] size-1.5 shrink-0 rounded-full bg-ink' />
                                    {item}
                                </li>
                            ))}
                        </ul>
                        <PageLink
                            href={`/services#${service.slug}`}
                            className='mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-ink underline decoration-ember decoration-2 underline-offset-4'
                        >
                            Full forecast
                            <ArrowUpRight className='size-4' />
                        </PageLink>
                    </div>
                </div>

                <button
                    type='button'
                    aria-expanded={open}
                    aria-controls={listId}
                    onClick={() => setOpen(value => !value)}
                    className='relative mt-auto inline-flex items-center gap-2 self-start pt-5 font-mono text-[11px] uppercase tracking-[0.14em] text-stone transition-colors hover:text-ink'
                >
                    <Plus className={cn('size-3.5 transition-transform duration-500 ease-expo group-hover:rotate-45', open && 'rotate-45')} />
                    {open ? 'Hide the small print' : 'The small print'}
                </button>
            </article>
        </Reveal>
    )
}

export const ForecastBoard: FC = () => {
    return (
        <section
            id='forecast-board'
            aria-labelledby='forecast-board-title'
            className='relative py-20 md:py-28'
        >
            <div className='mx-auto max-w-[1400px] px-5 md:px-10'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index='02'>Services</SectionLabel>
                        <SplitText
                            as='h2'
                            id='forecast-board-title'
                            lines={['The Forecast', 'Board']}
                            italic='forecast'
                            className='mt-5 text-[clamp(3rem,7vw,6.5rem)] font-bold leading-[0.88] tracking-[-0.045em]'
                        />
                    </div>
                    <Reveal
                        delay={150}
                        className='lg:col-span-5'
                    >
                        <p className='text-lg leading-relaxed text-stone'>
                            Six services, read like a weather map. Every tile shows a live reading. Hover one, or tap it, for the small print.
                        </p>
                        <PageLink
                            href='/services'
                            data-cursor='Open'
                            className='mt-5 inline-flex items-center gap-2 font-medium text-ink underline decoration-ember decoration-2 underline-offset-4'
                        >
                            Every service, in detail
                            <ArrowUpRight className='size-4' />
                        </PageLink>
                    </Reveal>
                </div>

                <div className='mt-14 grid gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3'>
                    {services.map((service, index) => (
                        <Tile
                            key={service.slug}
                            service={service}
                            delay={(index % 3) * 90}
                            settle={[-1.2, 0.8, -0.6, 1, -0.8, 1.2][index]}
                        />
                    ))}
                </div>
            </div>
        </section>
    )
}
